// src/operator/console/sessions-console.tsx
//
// Page « Sessions actives » : liste les sessions temporaires en cours sur le relay,
// rafraîchies toutes les 5 secondes, avec action « Rejoindre » qui
// renvoie vers /console/session?s=<code>.
//
// API : GET /api/sessions → tableau de { code, hostId, state? }.

import { For, Show, createSignal } from "solid-js";
import { useNavigate } from "@solidjs/router";
import { createQuery } from "@tanstack/solid-query";

interface SessionItem {
  code: string;
  hostId: string;
  state?: string;
  admissionShortCode?: string | null;
}

function isTemporarySession(session: SessionItem): boolean {
  return Boolean(session.admissionShortCode) || !String(session.hostId || "").trim();
}

async function fetchSessions(): Promise<SessionItem[]> {
  try {
    const r = await fetch("/api/sessions", { credentials: "include" });
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? (data as SessionItem[]) : [];
  } catch {
    return [];
  }
}

export function SessionsConsolePage() {
  const navigate = useNavigate();
  const [sessionCodeInput, setSessionCodeInput] = createSignal("");
  const [joinStatus, setJoinStatus] = createSignal<string | null>(null);

  const sessionsQ = createQuery<SessionItem[]>(() => ({
    queryKey: ["sessions-active"],
    queryFn: fetchSessions,
    refetchInterval: 5000,
    retry: false,
  }));

  const sessions = () => (sessionsQ.data ?? []).filter(isTemporarySession);

  function joinSession(code: string) {
    navigate(`/console/session?s=${code}`);
  }

  function openTypedSession() {
    const code = sessionCodeInput()
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();
    if (!code) return;
    if (code.length < 9) {
      setJoinStatus("Saisissez le code d'assistance complet à 9 caractères.");
      return;
    }
    setSessionCodeInput("");
    setJoinStatus(`Ouverture session ${code}`);
    joinSession(code);
  }

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Sessions actives
          <span class="plugins-console-sub">sessions temporaires WebSocket en cours sur le relay</span>
        </span>
        <div style={{ flex: "1 1 0" }} />
        <div style={{ display: "flex", gap: "8px", "align-items": "center" }}>
          <input
            class="input"
            style={{ width: "190px", "text-transform": "uppercase" }}
            placeholder="Code de session"
            value={sessionCodeInput()}
            onInput={(e) => setSessionCodeInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") openTypedSession();
            }}
          />
          <button class="btn btn--primary btn--xs" type="button" onClick={openTypedSession}>
            Ouvrir
          </button>
        </div>
        <button class="btn btn--xs" type="button" onClick={() => sessionsQ.refetch()}>
          ↺ Rafraîchir
        </button>
      </div>
      <Show when={joinStatus()}>
        <div style={{ padding: "0 0 10px", color: "var(--text-3)", "font-size": "12px" }}>{joinStatus()}</div>
      </Show>

      <div class="rmm-table-wrap">
        <table class="rmm-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>État</th>
              <th style={{ width: "120px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={sessions().length > 0}
              fallback={
                <tr>
                  <td colspan="3" style={{ color: "var(--text-4)", "text-align": "center", padding: "32px 0" }}>
                    Aucune session temporaire active
                  </td>
                </tr>
              }
            >
              <For each={sessions()}>
                {(s) => (
                  <tr>
                    <td>
                      <span
                        style={{
                          "font-family": "var(--font-mono)",
                          "font-size": "15px",
                          "font-weight": "600",
                          color: "var(--text-1)",
                          "letter-spacing": "0.5px",
                        }}
                      >
                        {s.code}
                      </span>
                    </td>
                    <td>
                      <span class="tag">{s.state ?? "—"}</span>
                    </td>
                    <td>
                      <button class="btn btn--primary btn--xs" type="button" onClick={() => joinSession(s.code)}>
                        Rejoindre
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>
    </div>
  );
}
